<script setup lang="ts">
import Epubjs, { Book } from 'epubjs';
import { onMounted, ref, toRefs, computed, shallowRef, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';
import useLocale from '../composables/useLocale';
import { UserInformation, NAVIGATION_ITEMS } from '../types';
import AudioPlayer from '../components/AudioPlayer.vue';
import PlayPauseBtn from '../components/Control/PlayPauseBtn.vue';
import SkipByControls from '../components/Control/SkipByControls.vue';
import TimeSkipControls from '../components/Control/TimeSkipControls.vue';
import useMediaStore from '../stores/media';
import useReaderStore from '../stores/reader';
import useNavigationTriggerStore from '../stores/navigation-trigger';
import useReaderEngine from '../engine/use-reader-engine';
import useAudioOnlyReader from '../engine/epubjs/audio-only-reader';
import useAudioOnlyNavigation from '../composables/use-audio-only-navigation';
import usePlaylistStore from '../stores/playlist';
import { parse } from '../util/manifest';
import { sendRequest } from '../util/request';
import { fileRequestHandlerWithCapacitor } from '../util/localFileReader';
import useSettingStore from '../stores/setting';
import PlaylistProgressSlider from '../components/Control/PlaylistProgressSlider.vue';
import TimeBasedProgressSlider from '../components/Control/TimeBasedProgressSlider.vue';
import AudioWaveIndicator from '../components/Control/AudioWaveIndicator.vue';
import SeekingModeToggleBtn from '../components/Control/SeekingModeToggleBtn.vue';
import PageRangeDisplay from '../components/PageRangeDisplay.vue';
import NarrationSpeed from '../components/settings/NarrationSpeed.vue';
import useUiStore from '../stores/ui';
import Languages from '../components/languages/Languages.vue';
import LanguageList from '../components/languages/LanguageList.vue';
import type { Manifest } from '../util/manifest';
import TableOfContentBtn from '../components/tableOfContent/TableOfContentBtn.vue';
import Navigation from '../components/tableOfContent/Navigation.vue';
import NavigationItems from '../components/tableOfContent/NavigationItems.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { faRectangleBeta } from '@fortawesome/pro-regular-svg-icons';
import AudioVolume from '../components/Control/AudioVolume.vue';
import { Capacitor } from '@capacitor/core';
import useBookCoverDimensions from '../composables/use-book-cover-dimensions';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import TimeDisplayUnderSlider from '../components/Control/TimeDisplayUnderSlider.vue';
import BookRemainingTime from '../components/Control/BookRemainingTime.vue';
import useAudioTimeDisplay from '../composables/use-audio-time-display';
import Settings from '../components/settings/Settings.vue';
import SettingsBtn from '../components/settings/SettingsBtn.vue';
import LanguageBtn from '../components/languages/LanguageBtn.vue';
import { useTextOverflow } from '../composables/use-text-overflow';
import { useContentSessionEnd } from '../composables/use-content-session-end';
import { sendBookReaderEventAnalytics } from '../composables/use-send-analytics';
import Mousetrap from 'mousetrap';
import SwitchModeBtn from '../components/Control/SwitchModeBtn.vue';
import type { SwitchBookPlayerModeEvent } from '../types';

library.add([faChevronLeft, faRectangleBeta]);

type Emits = {
  (e: 'selectTextLanguage', event: any): void;
  (e: 'close'): void;
  (e: 'goToLogin'): void;
  (e: 'bookError', error: string): void;
  (e: 'switchBookPlayerMode', event: SwitchBookPlayerModeEvent): void;
};
const emit = defineEmits<Emits>();

type Props = {
  userInformation: UserInformation;
  bookId: string;
  source: string | File | ArrayBuffer;
  startPosition?: string;
  showTooltips: boolean;
  onBeforeRequest?: (url: string) => Promise<void>;
  ignoreErrors?: boolean;
};
const props = defineProps<Props>();
const { userInformation, ignoreErrors, showTooltips } = toRefs(props);

const { smAndDown, xs, height } = useDisplay();
const { t } = useLocale();
const mediaStore = useMediaStore();
const { playing, isPlaying, hasMediaAvailable, currentTrack, isAtStartOfMedia, isAtEndOfMedia } =
  storeToRefs(mediaStore);
const { setMediaAvailable } = mediaStore;
const {
  isTimeBasedSeekingVisible,
  isSettingsVisible,
  isLanguageMenuVisible,
  isSmAndDownLandscape,
  isTableOfContentVisible,
  isVolumeSliderVisible,
  switchModeAvailable,
} = storeToRefs(useUiStore());
const {
  toggleVisibleSettings,
  toggleLanguageMenu,
  closeLanguageMenu,
  closeTableOfContentDrawer,
  toggleVisibleVolumeSlider,
  closeVisibleSettings,
} = useUiStore();
const readerStore = useReaderStore();
const { book, bookError, isLocalEpubFile, bookTitle, bookAuthors, bookCoverUrl } = storeToRefs(readerStore);
const { setBook, setBookError, setBookTitle, setBookAuthors } = readerStore;
const { initialise } = useReaderEngine();
const { setup: setupAudioOnly, switchAudioLanguage, getCurrentPlaylistIndex } = useAudioOnlyReader();
const { navigateNext, navigatePrevious, restoreLastPosition, goToContent } = useAudioOnlyNavigation();
const { setNavigationTrigger } = useNavigationTriggerStore();
const { isInitializing, playlist, isAtStartOfPlaylist, isAtEndOfPlaylist } = storeToRefs(usePlaylistStore());
const { coverRef, coverHeight, coverWidth, onImageLoaded } = useBookCoverDimensions();

const { isNarrationSpeedChangeAvailable, playbackRate } = storeToRefs(useSettingStore());
const {
  currentTimeFormatted,
  remainingTrackTimeFormatted,
  currentPlaylistTimeFormatted,
  remainingPlaylistTimeFormatted,
} = useAudioTimeDisplay();

const isLoading = ref(true);
const audioPlayerRef = ref<InstanceType<typeof AudioPlayer> | null>(null);
const settingsRef = ref<InstanceType<typeof Settings> | null>(null);
const languagesRef = ref<InstanceType<typeof Languages> | null>(null);
const settingsBtnRef = ref<InstanceType<typeof SettingsBtn> | null>(null);
const languageBtnRef = ref<InstanceType<typeof LanguageBtn> | null>(null);

const manifest = shallowRef<Manifest | null>(null);

const isDesktop = computed(() => Capacitor.getPlatform() === 'web' && !smAndDown.value);
// Detect small viewport height (like iPhone SE)
const isSmallHeight = computed(() => height.value < 700);

const bookTitleWithAuthors = computed(() => {
  if (!bookTitle.value) return '';
  const authorsText = bookAuthors.value ? ` ${t('book_player.label_by_authors', { authors: bookAuthors.value })}` : '';
  return bookTitle.value + authorsText;
});

const { scrollingTextEl, containerEl, hasOverflow, scrollDuration } = useTextOverflow();
const { endContentSession } = useContentSessionEnd();

const request = async (url: string, type: string) => {
  if (props.onBeforeRequest) {
    await props.onBeforeRequest(url);
  }
  return sendRequest(url, type);
};

const loadEpubBook = async (): Promise<Book> => {
  if (!props.source) {
    throw new Error('No book source provided');
  }

  // @ts-ignore missing types
  const epubBook = new Epubjs(props.source, {
    requestMethod: isLocalEpubFile.value ? fileRequestHandlerWithCapacitor : request,
  });

  epubBook.on('openFailed', (e: any) => {
    console.error('AudioOnly: openFailed', e);
    console.error('AudioOnly: Failed to open book at:', props.source);
    setBookError('error_fault_in_book_message');
    emit('bookError', 'error_fault_in_book_message');
  });

  await epubBook.ready;

  return epubBook;
};

const initializeAudioOnly = async () => {
  try {
    isLoading.value = true;

    const epubBook = await loadEpubBook();
    manifest.value = parse(epubBook);
    initialise(manifest.value, epubBook, isLocalEpubFile.value);

    setBook(epubBook);
    setBookTitle(manifest.value.title);
    setBookAuthors(manifest.value.creator);
    window.document.title = `${manifest.value.title} - ${manifest.value.creator}`;

    // Check for media availability
    const hasMediaAvailable = Object.keys(manifest.value.smilItems).length > 0;
    setMediaAvailable(hasMediaAvailable);

    if (!hasMediaAvailable) {
      console.warn('No audio content found in book');
      setBookError('No audio content available');
      emit('bookError', 'No audio content available');
      return;
    }

    // Setup audio-only mode
    await setupAudioOnly(manifest.value);

    // Restore last position if available
    await restoreLastPosition(
      props.bookId,
      userInformation.value.userIdentity.type === 'User' ? userInformation.value.userIdentity.profileId : undefined,
      props.startPosition,
    );

    sendBookReaderEventAnalytics('AUDIO_BOOK_OPENED');

    console.debug('Audio-only mode initialized successfully');
  } catch (error) {
    console.error('Failed to initialize audio-only mode:', error);
    setBookError('Failed to initialize audio player');
    emit('bookError', 'Failed to initialize audio player');
  } finally {
    isLoading.value = false;
  }
};

const handleTogglePlayer = () => {
  playing.value = !playing.value;
  sendBookReaderEventAnalytics('PLAY_PAUSE_CLICKED');
};

const handleSeekToTime = (time: number) => {
  audioPlayerRef.value?.handleSeekToTime(time, true);
  setNavigationTrigger('SLIDER');
  sendBookReaderEventAnalytics('AUDIO_LOCATION_CHANGED');
};

const handleSkipBackward = async (seconds: number) => {
  await audioPlayerRef.value?.seekByAmount(seconds, 'prev');
  setNavigationTrigger('TIME_SKIP_BACK');
  sendBookReaderEventAnalytics('AUDIO_LOCATION_CHANGED');
};

const handleSkipForward = async (seconds: number) => {
  await audioPlayerRef.value?.seekByAmount(seconds, 'next');
  setNavigationTrigger('TIME_SKIP_FORWARD');
  sendBookReaderEventAnalytics('AUDIO_LOCATION_CHANGED');
};

const onSettingsReset = () => {
  playbackRate.value = 1;
  sendBookReaderEventAnalytics('SETTINGS_RESET');
};

const onNarrationSpeedChange = () => {
  sendBookReaderEventAnalytics('NARATION_SPEED_CHANGED');
};

const handleAudioLanguageChange = async () => {
  if (!manifest.value) return;

  //Find current track index
  const currentPlaylistIndex = getCurrentPlaylistIndex();
  let currentTrackIndex = playlist.value[currentPlaylistIndex.value].tracks.findIndex(
    (track) => track.audio.src === currentTrack.value?.audio.src,
  );

  // If track index not found default to 0
  if (currentTrackIndex === -1) {
    currentTrackIndex = 0;
  }

  await switchAudioLanguage(manifest.value, currentTrackIndex);
  sendBookReaderEventAnalytics('AUDIO_LANGUAGE_SELECTED');
};

const isAllDrawersClosed = computed(
  () =>
    !isTableOfContentVisible.value &&
    !isSettingsVisible.value &&
    !isVolumeSliderVisible.value &&
    !isLanguageMenuVisible.value,
);

const isFocusOnBody = () => {
  const activeEl = document.activeElement;

  if (!activeEl) return false;

  return activeEl?.tagName === 'BODY';
};

onMounted(() => {
  initializeAudioOnly();

  const platform = Capacitor.getPlatform();
  if (xs.value && (platform === 'ios' || platform === 'android')) {
    try {
      ScreenOrientation.lock({ orientation: 'portrait' });
    } catch (error) {
      console.debug('ScreenOrientation lock failed', error);
    }
  }

  // Space bar for play/pause
  Mousetrap.bind('space', () => {
    if (!isFocusOnBody()) return true; //avoid overriding other space functionality (e.g. pressing down focused buttons).
    handleTogglePlayer();
    return false;
  });

  // 'k' for play/pause
  Mousetrap.bind('k', () => {
    handleTogglePlayer();
    return false;
  });

  // Esc button for closing down
  Mousetrap.bind('esc', () => {
    if (isAllDrawersClosed.value) {
      emit('close');
      return false;
    }

    closeTableOfContentDrawer();
    closeLanguageMenu();
    toggleVisibleVolumeSlider(false);
    closeVisibleSettings();
    return false;
  });
});

onUnmounted(() => {
  // It's placed here to let session events to be processed in child components onBeforeUnmount
  endContentSession();

  const platform = Capacitor.getPlatform();
  if (xs.value && (platform === 'ios' || platform === 'android')) {
    try {
      ScreenOrientation.unlock();
    } catch (error) {
      console.debug('ScreenOrientation unlock failed', error);
    }
  }

  Mousetrap.unbind('space');
  Mousetrap.unbind('k');
  Mousetrap.unbind('esc');
});
</script>

<template>
  <div class="audio-only-container d-flex flex-column bg-surface" data-testid="bp-audio-only-view">
    <div
      class="content-area d-flex flex-column align-center flex-grow-1 order-2"
      :class="{ 'justify-center': !smAndDown, 'mt-10': smAndDown }"
      v-if="(!bookError || ignoreErrors) && book"
    >
      <div class="content-wrapper d-flex flex-column">
        <div v-if="bookTitle && xs" class="book-title-mobile text-center mb-5">
          <h1
            ref="containerEl"
            class="text-body-1 clamp-text"
            :class="{ 'has-overflow': hasOverflow }"
            :style="{ '--scroll-duration': `${scrollDuration}s` }"
          >
            <span ref="scrollingTextEl" class="scrolling-text">{{ bookTitleWithAuthors }}</span>
          </h1>
        </div>
        <div
          class="player-section d-flex flex-column"
          :class="{
            'ga-2': xs && isSmallHeight,
            'ga-4': (!isSmallHeight || !xs) && !isSmAndDownLandscape,
          }"
        >
          <div class="d-flex flex-column justify-center align-center order-1">
            <AudioPlayer ref="audioPlayerRef" @go-to-next-play-list-segment="navigateNext" />
            <SkipByControls
              :show-tooltips="showTooltips"
              :disabled-previous="isAtStartOfPlaylist"
              :disabled-next="isAtEndOfPlaylist"
              @go-to-previous="navigatePrevious"
              @go-to-next="navigateNext"
              :button-size="xs || isSmAndDownLandscape ? 56 : 80"
              :icon-size="xs || isSmAndDownLandscape ? 'xl' : '3x'"
            >
              <TimeSkipControls
                :show-tooltips="showTooltips"
                :disabled-backward="isAtStartOfMedia"
                :disabled-forward="isAtEndOfMedia"
                @skip-backward="handleSkipBackward"
                @skip-forward="handleSkipForward"
                :button-size="xs || isSmAndDownLandscape ? 56 : 80"
                :icon-size="xs || isSmAndDownLandscape ? '2xl' : '3x'"
              >
                <PlayPauseBtn
                  :is-playing="isPlaying"
                  :show-tooltips="showTooltips"
                  :isLoading="isLoading"
                  @play-pause="handleTogglePlayer"
                  :icon-size="xs || isSmAndDownLandscape ? '3x' : '5x'"
                  :buttonSize="xs || isSmAndDownLandscape ? 80 : 120"
                />
              </TimeSkipControls>
            </SkipByControls>
          </div>
          <!-- Loading state -->
          <div v-if="isLoading" class="d-flex flex-column align-center ga-2">
            <v-skeleton-loader type="image" :width="coverWidth" :height="coverHeight" />
            <v-skeleton-loader type="text" class="w-100" />
          </div>
          <!-- Loaded state (image & slider)-->
          <div v-else class="d-flex flex-column align-center ga-2">
            <v-img
              ref="coverRef"
              :src="bookCoverUrl"
              lazy-src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mO88R8AArUB2TtNwp8AAAAASUVORK5CYII="
              :transition="false"
              :width="coverWidth"
              :height="coverHeight"
              :alt="
                bookTitle
                  ? t('book_player.label_book_cover_with_title', { bookTitle })
                  : t('book_player.label_book_cover')
              "
              @load="onImageLoaded"
            >
            </v-img>
            <div class="d-flex flex-column align-center w-100">
              <PageRangeDisplay v-if="!isInitializing" />
              <PlaylistProgressSlider v-if="!isTimeBasedSeekingVisible" @go-to-content="goToContent">
                <template #prepend>
                  <AudioWaveIndicator :is-playing="isPlaying" />
                </template>
                <template #append>
                  <SeekingModeToggleBtn :show-tooltips="showTooltips" />
                </template>
                <template #bottom>
                  <TimeDisplayUnderSlider
                    :left-time="currentPlaylistTimeFormatted"
                    :right-time="remainingPlaylistTimeFormatted"
                  />
                </template>
              </PlaylistProgressSlider>
              <TimeBasedProgressSlider v-else @seek-to-time="handleSeekToTime">
                <template #prepend>
                  <AudioWaveIndicator :is-playing="isPlaying" />
                </template>
                <template #append>
                  <SeekingModeToggleBtn :show-tooltips="showTooltips" />
                </template>
                <template #bottom>
                  <TimeDisplayUnderSlider :left-time="currentTimeFormatted" :right-time="remainingTrackTimeFormatted" />
                </template>
              </TimeBasedProgressSlider>
              <BookRemainingTime v-if="!isInitializing" />
            </div>
          </div>
        </div>
      </div>
      <!-- Bottom controls -->
      <v-toolbar
        class="bottom-controls bg-surface order-3"
        tag="nav"
        elevation="0"
        data-testid="bp-audio-only-controls"
      >
        <div
          class="d-flex justify-center align-center w-100"
          :class="{ 'justify-space-around': xs, 'ga-2': smAndDown }"
        >
          <TableOfContentBtn :show-tooltips="showTooltips" :btn-size="smAndDown ? 70 : 56" />
          <SettingsBtn
            ref="settingsBtnRef"
            :has-settings="settingsRef?.hasSettings"
            :is-settings-visible="isSettingsVisible"
            :show-tooltips="showTooltips"
            :btn-size="smAndDown ? 70 : 56"
            @click="!smAndDown ? toggleVisibleSettings(!isSettingsVisible) : undefined"
          />
          <AudioVolume v-if="isDesktop" :audio-btn-size="xs ? 70 : 56" />
          <LanguageBtn
            ref="languageBtnRef"
            :has-languages="languagesRef?.isAudioSwitchingAvailable"
            :is-language-menu-visible="isLanguageMenuVisible"
            :show-tooltips="showTooltips"
            :btn-size="smAndDown ? 70 : 56"
            @click="!smAndDown ? toggleLanguageMenu(!isLanguageMenuVisible) : undefined"
          />
          <SwitchModeBtn
            v-if="switchModeAvailable"
            :btn-size="xs ? 70 : 56"
            @switch-book-player-mode="emit('switchBookPlayerMode', $event)"
          />
        </div>
      </v-toolbar>
      <Settings
        ref="settingsRef"
        :show-tooltips="showTooltips"
        :activator="settingsBtnRef?.settingsBtn"
        @reset="onSettingsReset"
        @close="toggleVisibleSettings(false)"
      >
        <div class="d-flex flex-column" :class="{ 'pl-2 pr-2': smAndDown }">
          <NarrationSpeed
            v-if="isNarrationSpeedChangeAvailable"
            :has-media-available="hasMediaAvailable"
            v-model="playbackRate"
            @update:model-value="onNarrationSpeedChange"
          />
          <slot name="customSettings" />
        </div>
      </Settings>
      <Languages
        ref="languagesRef"
        :show-tooltips="showTooltips"
        :activator="languageBtnRef?.languageBtn"
        @select-audio-language="handleAudioLanguageChange"
        @close="toggleLanguageMenu(false)"
      >
        <template #audio-languages="{ items, audioLanguage, isAvailable, onSelect }">
          <LanguageList v-if="isAvailable" :items="items" :compare="audioLanguage" :on-select="onSelect" />
        </template>
      </Languages>
      <Navigation v-slot="slotProps" :navigation-items="[NAVIGATION_ITEMS.PLAYLIST]">
        <NavigationItems
          :tab="slotProps.tab"
          :search="slotProps.search"
          :navigation-items="slotProps.navigationItems"
          :book-id="bookId"
          @go-to-content="goToContent"
          @reset-search="slotProps.resetSearch"
        />
      </Navigation>
    </div>
    <!--Toolbar-->
    <v-toolbar class="bg-transparent order-1" tag="header" :density="xs ? 'compact' : 'default'">
      <template #prepend>
        <slot name="close-btn" :close-player="() => emit('close')">
          <v-btn
            variant="text"
            color="on-surface"
            :ripple="false"
            :aria-label="t('button_close_app')"
            @click="emit('close')"
            :size="xs ? 48 : 56"
          >
            <FontAwesomeIcon :size="xs ? 'lg' : 'xl'" icon="fas fa-chevron-left" />
            <v-tooltip
              activator="parent"
              location="bottom"
              :text="t('button_close_app') || 'Close'"
              :disabled="!showTooltips"
            />
          </v-btn>
        </slot>
        <h1 v-if="bookTitle && !xs" class="me-4 title">
          {{ bookTitle }}
          <span v-if="bookAuthors"> {{ t('book_player.label_by_authors', { authors: bookAuthors }) }}</span>
        </h1>
      </template>
      <v-spacer />
      <template #append>
        <div class="beta-icon">
          <FontAwesomeIcon icon="far fa-rectangle-beta" :size="xs ? 'xl' : '2xl'" class="text-on-surface" />
          <v-tooltip
            activator="parent"
            location="bottom"
            :text="t('book_player.label_audio_only_beta_tooltip')"
            :disabled="!showTooltips"
          />
        </div>
      </template>
    </v-toolbar>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as v;

$toolbar-height-desktop: 90px;
$toolbar-height-mobile: 80px;

.audio-only-container {
  overflow: hidden;
  height: 100%;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.content-area {
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: calc($toolbar-height-desktop + 8px);

  @media #{map.get(v.$display-breakpoints, 'xs')} {
    padding-bottom: calc($toolbar-height-mobile + 8px);
  }
}

.content-wrapper {
  max-width: 100vw;

  @media #{map.get(v.$display-breakpoints, 'sm-and-up')} {
    min-width: 450px;
  }
}

.bottom-controls {
  position: fixed !important;
  bottom: 0 !important;
  width: 100vw;
  z-index: 999;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  height: $toolbar-height-desktop;

  @media #{map.get(v.$display-breakpoints, 'xs')} {
    height: $toolbar-height-mobile;
  }
}

:deep(.v-skeleton-loader__image) {
  height: 100% !important;
}

@keyframes scroll-text {
  0% {
    transform: translateX(1.5rem);
  }
  10% {
    transform: translateX(1.5rem);
  }
  45% {
    transform: translateX(calc(-100% + 100vw - 4rem));
  }
  55% {
    transform: translateX(calc(-100% + 100vw - 4rem));
  }
  90% {
    transform: translateX(1.5rem);
  }
  100% {
    transform: translateX(1.5rem);
  }
}

.clamp-text {
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  justify-content: center;
  width: 100%;

  .scrolling-text {
    display: inline-block;
  }

  &.has-overflow {
    justify-content: flex-start;

    .scrolling-text {
      min-width: 100%;
      animation: scroll-text var(--scroll-duration, 18s) linear infinite;
    }
  }
}

.title {
  font-weight: 400 !important;
  font-size: 1.25rem !important;
  white-space: nowrap;
  overflow: hidden;

  span {
    display: inline-block;
  }
}

.beta-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: help;
  padding: 0 8px;
}
</style>
